import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Lock, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { usePreferences } from "@/hooks/useUserPreferences";
import { useFeature } from "@/lib/features";
import { visibleFacts, isFreeTierFact } from "@/lib/familyProfileTier";
import { FOCUS_AREAS } from "@/lib/focusAreas";
import { ProUpsellLink, useProUpsellToast } from "@/components/ProUpsell";
import { type KidColorRecord } from "@/lib/kidColors";
import { usesTextarea } from "@/lib/factEditControl";
import {
  WEEKDAY_LABELS,
  applyActivitySchedule,
  isUnresolvedActivityFact,
  parseActivityRecurrence,
} from "@/lib/activityRecurrence";
import { assertWriteLanded } from "@/lib/scheduledTaskEdit";
import { isUnverifiedFactWrite, rememberFamilyFact } from "@/lib/rememberFamilyFact";
import { familyFactAddErrorCopy } from "@/lib/familyFactAddCopy";
import { reportError } from "@/lib/reportError";
import {
  LEARNED_FILTER_MIN_FACTS,
  LEARNED_GROUP_CAP,
  buildLearnedView,
  countFacts,
} from "@/lib/learnedListView";

/**
 * Family Profile — the trust surface of the Family Memory system.
 *
 * Shows every fact Kin has stored about the family (family_context), grouped
 * by subject (each kid with their color accent, then Household, Providers, and
 * anything else). Every fact shows its value, where it came from (source pill),
 * when it was learned, and edit + delete controls.
 *
 * Reads/writes family_context ONLY, through the TYPED client. It never touches
 * kin-chat, the dispatcher, the [LEARN] pipeline, or family-memory-context.ts.
 *
 * ⚠️ This file cast the client to `any` until #1210, justified by a comment
 * claiming family_context "isn't in the generated types". That stopped being
 * true, the comment did not, and the cast hid a wrong column name for seven
 * weeks: every query here selected a column that does not exist on this
 * table. PostgREST answered 42703, the error was swallowed to a console.warn,
 * and the screen rendered its empty state — indistinguishable from an account
 * with no facts. Nobody could see it, on any account, since PR #66.
 *
 * The creation timestamp on family_context is `learned_at`. Do not "fix" a
 * 42703 here by following Postgres's HINT: it suggests by spelling similarity
 * and proposes `updated_at`, which is the row's MUTATION time and moves every
 * time a fact is edited. Take the column from types.ts, never from the hint.
 *
 * Keep this file on the typed client so the next wrong column is a build
 * error rather than a silent empty screen.
 *
 * Kid NAME and COLOR editing intentionally live in MyKidsSection — this screen
 * links there for those and never duplicates them. One source of truth.
 */

/**
 * #762 — where a row in this hub actually came from.
 *
 * ⚠️ NOT the same as `Fact.source` below, which is `family_context.source` (the
 * column saying which flow taught Kin the fact: "pro_onboarding", "chat", …).
 * This says which TABLE the row lives in, because two of the three cannot be
 * archived or edited the way a family_context row can.
 *
 * Absent means family_context, so every existing fact keeps its exact shape and
 * no existing read or write path changes — the guardrail on #762.
 */
type FactOrigin = "family_providers" | "focus_area";

interface Fact {
  id: string;
  /** Absent = a real `family_context` row. See FactOrigin. */
  origin?: FactOrigin;
  category: string;
  subject: string | null;
  key: string;
  value: string;
  source: string | null;
  confidence: string | null;
  metadata: Record<string, unknown> | null;
  learned_at: string | null;
}

/** Humanize a snake_case fact key into a label. "birthdate" -> "Birthday". */
function humanizeKey(key: string): string {
  if (key === "birthdate") return "Birthday";
  return key
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Format a YYYY-MM-DD birthdate as "Mar 12, 2020 · age 6". Mirrors MyKids. */
function formatBirthdate(isoDate: string): string {
  const parts = isoDate.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return isoDate;
  const [yr, mo, dy] = parts;
  const monthName = new Date(yr, mo - 1, dy).toLocaleString("en-US", { month: "short" });
  const today = new Date();
  let age = today.getFullYear() - yr;
  if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < dy)) {
    age--;
  }
  return `${monthName} ${dy}, ${yr} · age ${age}`;
}

/** Present a fact's value for reading. Birthdays get the friendly age format. */
function formatValue(key: string, value: string): string {
  if (key === "birthdate" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return formatBirthdate(value);
  }
  return value;
}

/** "Learned Jul 3" style stamp from an ISO timestamp. */
function formatLearnedDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `Learned ${d.toLocaleString("en-US", { month: "short", day: "numeric" })}`;
}

/** Source pill styling + copy. Falls back gracefully for unknown sources. */
function sourceMeta(source: string | null): { label: string; bg: string; color: string } {
  switch (source) {
    case "onboarding":
      return { label: "From setup", bg: "#EDEBFF", color: "#5050C8" };
    case "chat":
      return { label: "From chat", bg: "#FDEEF6", color: "#B23A7A" };
    case "manual":
      return { label: "You added", bg: "#E7F5EF", color: "#2E8B69" };
    case "pro_setup":
      return { label: "Pro setup", bg: "#FFF3E0", color: "#C4791F" };
    default:
      return { label: source ? humanizeKey(source) : "Stored", bg: "#F0F0FF", color: "#54525C" };
  }
}

/** A chat-learned fact is "guessed" until the user confirms it. */
function isUnconfirmedGuess(f: Fact): boolean {
  return f.source === "chat" && !(f.metadata && (f.metadata as any).confirmed === true);
}

const DISMISS_KEY = "kin_family_profile_setup_dismissed";

function caseInsensitiveEq(a: string | null, b: string | null): boolean {
  return (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
}

interface Group {
  id: string;
  title: string;
  accent: string;
  /**
   * The `family_context.category` an ADD from this group would insert.
   *
   * ⚠️ null for a group that is not backed by family_context (#762's Providers
   * and Focus Areas). `saveAdd` inserts this value verbatim, and the column has
   * a CHECK constraint — #1241 was exactly this: a group whose category was not
   * in the constraint would take a 23514 that the UI swallowed to a
   * console.warn. Making it nullable means the compiler, not a reviewer, is
   * what stops an un-insertable group growing an Add button.
   */
  category: string | null;
  subject: string | null;
  linkToMyKids: boolean;
  facts: Fact[];
  /**
   * Copy to show INSTEAD of facts when this group is empty. A group with this
   * set survives the non-empty filter at the end of the `groups` memo, so it
   * renders as an invitation rather than vanishing. Only the "You" group uses
   * it today — see the comment there for why absence was the bug.
   */
  emptyCopy?: string;
}

/**
 * #762 — the Pro setup flow writes providers to `family_providers`, a separate
 * table, and `loadFacts` only ever queried `family_context`. So the Providers
 * group could never show a pediatrician or a dentist: kin-chat reads them
 * correctly and this hub could not. Column names read from the real schema,
 * not assumed from family_context's shape.
 */
interface ProviderRow {
  id: string;
  type: string;
  provider_name: string;
  linked_kid_name: string | null;
  last_visit: string | null;
  cadence_months: number | null;
}

const FOCUS_ACCENT = "#4D908E";

/**
 * #763 — stable empty references for the free-tier view.
 *
 * Module constants, not `[]` literals: a fresh array every render would change
 * the identity of a `useMemo` dependency on every pass and rebuild the whole
 * group list for a free account continuously.
 */
const EMPTY_PROVIDER_ROWS: ProviderRow[] = [];
const EMPTY_FOCUS_AREAS: string[] = [];

/**
 * ⚠️ Display-only category labels for #762's synthesized rows. Deliberately NOT
 * written as bare string literals at the Fact sites: `family_context.category`
 * has a CHECK constraint, and the #1241 guard scans this file for category
 * literals precisely because one that is not in the constraint reads as legal
 * and fails at 23514. These rows are never inserted — they carry `origin` — and
 * naming the constants says so where a literal would have looked like a claim
 * about the vocabulary.
 */
const PROVIDER_FACT_CATEGORY = "family_providers";
const FOCUS_FACT_CATEGORY = "focus_areas";

const HOUSEHOLD_ACCENT = "#5050C8";
const PROVIDER_ACCENT = "#577590";
const SELF_ACCENT = "#B23A7A";

/** Case-insensitive color lookup mirroring MyKidsSection.findColor. */
function findColor(name: string, colors: KidColorRecord): string | null {
  if (colors[name]) return colors[name];
  const lc = name.toLowerCase();
  for (const k of Object.keys(colors)) {
    if (k.toLowerCase() === lc) return colors[k];
  }
  return null;
}

interface FamilyProfileSectionProps {
  // Reports the loaded fact count upward so a parent summary row (Settings'
  // "What Kin's learned") can show a live count without duplicating this
  // component's own family_context fetch.
  onFactCountChange?: (count: number) => void;
  // #667/#666 — a fact id to auto-open for editing once facts finish
  // loading (Today's birthday/activity layers deep-link here via
  // `/settings?editFact=<id>#learned-row`). Undefined/not-found is a no-op.
  targetFactId?: string;
}

export default function FamilyProfileSection({ onFactCountChange, targetFactId }: FamilyProfileSectionProps = {}) {
  const { user, loading: authLoading, adminLoaded, proLoaded } = useAuth();
  const { prefs, loading: prefsLoading, updatePreferences } = usePreferences();
  const canEdit = useFeature("familyProfileEdit");
  /**
   * #763 — may this account SEE Pro-collected facts?
   *
   * The same entitlement as editing them today, and deliberately its OWN name
   * anyway. Before #763 the view gate and the edit gate were genuinely
   * different rules (view was free), and giving the view rule a name is what
   * stops the next reader collapsing `canSeeProFacts` back into `canEdit` as a
   * tidy-up and quietly re-deciding a product question.
   */
  const canSeeProFacts = canEdit;
  /**
   * ⚠️ Whether the tier is KNOWN YET — not what it is.
   *
   * `useFeature` returns false while auth/admin/pro are still loading, which
   * is the right default for an ADD button (it stays locked for an instant)
   * and the wrong one for a whole screen: a Pro account would render the free
   * view first and her facts would appear to vanish and come back. So the
   * screen waits, and the wait reuses the "Loading…" branch that is already
   * there for the facts fetch.
   *
   * These are readiness flags, not a second copy of the gate — the gate rule
   * stays in `useFeature`. Reimplementing `isPro || (isAdmin && adminPreview)`
   * here is the thing this must never become.
   */
  const tierResolved = !authLoading && adminLoaded && proLoaded;
  const proUpsellToast = useProUpsellToast();

  const [facts, setFacts] = useState<Fact[]>([]);
  // #762 — kept in its OWN state, not merged into `facts`. `facts` is what
  // onFactCountChange reports and what every family_context write path reads;
  // folding another table into it would change both. Additive, per the issue.
  const [providerRows, setProviderRows] = useState<ProviderRow[]>([]);
  const [providersFailed, setProvidersFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dismissedSetup, setDismissedSetup] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  // #1242 — per-group cap, "Show all", and a filter. Kasandra chose this shape
  // 2026-09-12. Every hidden fact stays one tap away; see learnedListView.ts.
  const [factFilter, setFactFilter] = useState("");
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(() => new Set());
  // Groups she collapsed. Beats a just-added or deep-linked pin, so "Show fewer"
  // always works (Copilot on #1614); an open editor still can't be hidden.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());
  // The fact she just added. Pinned visible so a save can never land below the cap.
  const [lastAddedFactId, setLastAddedFactId] = useState<string | null>(null);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const editInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // #789 — day/time picker attached to the edit row of an activity fact Kin
  // stored but couldn't place on a day. Held here, not in a second editor:
  // the existing Save/Cancel buttons drive it.
  const [scheduleFactId, setScheduleFactId] = useState<string | null>(null);
  const [scheduleDays, setScheduleDays] = useState<number[]>([]);
  const [scheduleTime, setScheduleTime] = useState("");

  const closeEdit = useCallback(() => {
    setEditingId(null);
    setScheduleFactId(null);
    setScheduleDays([]);
    setScheduleTime("");
  }, []);

  const toggleScheduleDay = (day: number) =>
    setScheduleDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );

  // Inline add state (keyed by group id)
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [addKey, setAddKey] = useState("");
  const [addValue, setAddValue] = useState("");

  // #1210 — a failed read must NEVER look like an account with no facts.
  // Before this flag existed, a 42703 set `facts` to [] and the screen rendered
  // "Kin hasn't learned much yet 🌱", so seven weeks of total breakage read as
  // "you haven't told me anything". Three states now, not two: loading,
  // loaded-and-empty, and loadFailed.
  const [loadFailed, setLoadFailed] = useState(false);

  const loadFacts = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("family_context")
        .select("id, category, subject, key, value, source, confidence, metadata, learned_at")
        .eq("user_id", user.id)
        .eq("archived", false)
        .order("learned_at", { ascending: true });
      if (error) {
        // console.error, not warn: warn is invisible to Sentry, which is how
        // this survived undetected. See #1227 for the repo-wide sweep.
        console.error("[family-profile] load failed", error);
        setLoadFailed(true);
      } else if (Array.isArray(data)) {
        setLoadFailed(false);
        setFacts(data as Fact[]);
      }
    } catch (e) {
      console.error("[family-profile] load threw", e);
      setLoadFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  /**
   * #762 — providers, from the table they actually live in.
   *
   * A sibling fetch rather than a change to loadFacts: a failure here must not
   * blank the family_context facts, and #1227's rule applies — a failed read
   * is not an empty list, so it gets its own flag and its own message instead
   * of rendering as "no providers".
   */
  const loadProviders = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("family_providers")
        .select("id, type, provider_name, linked_kid_name, last_visit, cadence_months")
        .eq("user_id", user.id)
        .eq("archived", false)
        .order("provider_name", { ascending: true });
      if (error || !Array.isArray(data)) {
        console.error("[family-profile] provider load failed", error);
        setProvidersFailed(true);
        return;
      }
      setProvidersFailed(false);
      setProviderRows(data as ProviderRow[]);
    } catch (e) {
      console.error("[family-profile] provider load threw", e);
      setProvidersFailed(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    void loadProviders();
  }, [user, loadProviders]);

  useEffect(() => {
    if (!user || prefsLoading) return;
    void loadFacts();
  }, [user, prefsLoading, loadFacts]);

  /**
   * #763 — the three sources this hub renders, AFTER the tier filter.
   *
   * ⚠️ ONE choke point, not three parallel filters. The groups memo, the
   * empty/locked state (`hasFacts`) and the count reported to Settings all
   * read from these, so a free account cannot end up with a screen that
   * disagrees with its own count or its own empty state. Three hand-synced
   * copies of one rule is this codebase's most common defect shape (#361,
   * #470, #515, and the reason `contactListEdit.ts` exists).
   *
   * Providers and focus areas are Pro end to end — `family_providers` is
   * written only by the Pro setup flow and by kin-chat's Pro memory tools, and
   * focus areas are chosen in Pro setup — so for a free account they are not
   * filtered, they are simply absent.
   *
   * ⚠️ These derive from `facts` / `providerRows` for DISPLAY only. Every
   * write path below still operates on the full state: `setFacts` must not
   * learn about the filter or a Pro account's optimistic update would be
   * computed against a truncated list.
   */
  const shownFacts = useMemo(
    () => visibleFacts(facts, canSeeProFacts),
    [facts, canSeeProFacts],
  );
  const shownProviderRows = useMemo(
    () => (canSeeProFacts ? providerRows : EMPTY_PROVIDER_ROWS),
    [providerRows, canSeeProFacts],
  );
  const shownFocusAreas = useMemo(
    () =>
      canSeeProFacts && Array.isArray(prefs.focus_areas)
        ? prefs.focus_areas
        : EMPTY_FOCUS_AREAS,
    [prefs.focus_areas, canSeeProFacts],
  );

  useEffect(() => {
    if (!loaded) return;
    // #1210 — do not report a count when the read failed. Reporting 0 would
    // make the Settings row read "Getting started", which is the same lie the
    // empty state told: a broken query presented as an empty account.
    if (loadFailed) return;
    // ⚠️ #763, Copilot review on #1321 — WAIT FOR THE TIER HERE TOO.
    //
    // The render already waits (`!loaded || !tierResolved`), but this effect
    // does not render: it reports upward to Settings, which draws the count on
    // the COLLAPSED row, outside this component and outside that gate. So
    // without this line a Pro account whose facts resolve before her
    // entitlement does would publish the filtered count — "1 thing" — and then
    // jump to the real one. Exactly the flash `tierResolved` exists to stop,
    // one branch over, and invisible from inside this component.
    if (!tierResolved) return;
    // #763 — the VISIBLE count. Reporting `facts.length` on a free account
    // would put "12" beside a section showing two, which is the same
    // absence-reads-wrong shape #1210 fixed one branch over: a number that
    // contradicts the screen it labels.
    onFactCountChange?.(shownFacts.length);
  }, [loaded, loadFailed, tierResolved, shownFacts, onFactCountChange]);

  useEffect(() => {
    if (!editingId) return;
    setTimeout(() => {
      const el = editInputRef.current;
      if (!el) return;
      el.focus();
      // Open at content height rather than at one row and make her scroll to
      // discover the rest of what she already wrote.
      if (el instanceof HTMLTextAreaElement) {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      }
    }, 50);
  }, [editingId]);

  const kidColors: KidColorRecord = useMemo(() => {
    const raw = prefs.known_kid_colors;
    return raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as KidColorRecord) : {};
  }, [prefs.known_kid_colors]);

  const kidNames: string[] = useMemo(
    () => (prefs.known_kid_names || []).filter((n) => typeof n === "string" && n.trim().length > 0),
    [prefs.known_kid_names]
  );

  // Group facts by subject: HER first, then known kids (in order), then any
  // other kid subjects, then Household, Providers, then any remaining
  // categories.
  const groups: Group[] = useMemo(() => {
    const out: Group[] = [];
    const used = new Set<Fact>();

    // #1241 — her own facts (category='self'), first.
    //
    // Kasandra, 2026-08-31, right after #1210 made this screen work at all:
    // "facts are working! but only appearing for my kids, should facts also be
    // showing up for me?"
    //
    // They were not missing. `self` is a legal category (the 20260710 v1.3
    // migration added it) and the ProSetupFlow's "You" card writes it, so a
    // self fact already rendered — but it fell through to the leftover-category
    // bucket at the bottom of this memo and got a machine-made "Self" heading
    // from humanizeKey, in the default household accent, below Providers. This
    // relocates and names those rows; it does not surface hidden ones.
    //
    // First rather than last on purpose. The complaint was that she was absent
    // from her own family's screen, and putting her under everyone else
    // reproduces that quietly. Moving it is one `out.push` position.
    //
    // kin-chat already treats these as their own thing — index.ts:853 pulls
    // selfFacts into an "About her" section specifically so the 8-item cap
    // can't crowd them out. This screen was the only surface that didn't.
    const selfFacts = shownFacts.filter((f) => f.category === "self");
    selfFacts.forEach((f) => used.add(f));
    out.push({
      id: "self",
      title: "You",
      accent: SELF_ACCENT,
      category: "self",
      subject: null,
      linkToMyKids: false,
      facts: selfFacts,
      // Shown only when she has no self facts AND can actually add one. On
      // free tier the add button is an upsell and chat can't write self facts
      // either (kin-chat gates MEMORY_TOOLS on memoryEnabled), so the
      // invitation would be a dead end — same reasoning as the #789 needsDay
      // marker below. The group still renders normally if a free account has
      // self facts from an earlier Pro period.
      //
      // This costs exactly one row, and only while it is empty — it
      // self-destructs on her first self fact. That is the whole concession to
      // #1242 (this list has no length cap): an absent group is invisible,
      // which is the bug, and a permanent empty group is clutter forever.
      emptyCopy: canEdit
        ? "Kin's looking out for you too, not just the crew. Tell Kin something about you — what you do, what you're into, what you'd rather Kin didn't forget."
        : undefined,
    });

    const takeKidFacts = (name: string) =>
      shownFacts.filter((f) => f.category === "kid" && caseInsensitiveEq(f.subject, name));

    // Known kids, in their saved order.
    for (const name of kidNames) {
      const kf = takeKidFacts(name);
      kf.forEach((f) => used.add(f));
      out.push({
        id: `kid::${name.toLowerCase()}`,
        title: name,
        accent: findColor(name, kidColors) ?? HOUSEHOLD_ACCENT,
        category: "kid",
        subject: name,
        linkToMyKids: true,
        facts: kf,
      });
    }

    // Any kid subjects not in the known list (e.g. chat-learned before add).
    const orphanKidSubjects = Array.from(
      new Set(
        shownFacts
          .filter((f) => f.category === "kid" && !used.has(f) && f.subject)
          .map((f) => f.subject as string)
      )
    );
    for (const name of orphanKidSubjects) {
      const kf = shownFacts.filter(
        (f) => f.category === "kid" && !used.has(f) && caseInsensitiveEq(f.subject, name)
      );
      kf.forEach((f) => used.add(f));
      out.push({
        id: `kid::${name.toLowerCase()}`,
        title: name,
        accent: HOUSEHOLD_ACCENT,
        category: "kid",
        subject: name,
        linkToMyKids: true,
        facts: kf,
      });
    }

    // Household.
    const household = shownFacts.filter((f) => f.category === "household" && !used.has(f));
    household.forEach((f) => used.add(f));
    if (household.length) {
      out.push({
        id: "household",
        title: "Household",
        accent: HOUSEHOLD_ACCENT,
        category: "household",
        subject: null,
        linkToMyKids: false,
        facts: household,
      });
    }

    // Providers.
    //
    // ⚠️ The category is `provider_ref`, NOT `provider`. This filter read
    // "provider" until #1241 and therefore could not match a single row that
    // is legal to store: the family_context_category_check constraint
    // (20260710_v13_self_category_prefs_sizecheck.sql) permits exactly
    // kid, pet, provider_ref, household, travel, food, purchase_habit,
    // preference, other, self. kin-chat writes provider_ref (index.ts:683,
    // :865) and so does the save_family_fact tool schema.
    //
    // The rows were not invisible — they fell through to the leftover bucket
    // under a "Provider Ref" heading. But `category` here is also what
    // saveAdd() INSERTS, so before this fix an add from a rendered Providers
    // group would have written an illegal category, taken a 23514 check
    // violation, and shown "Couldn't add that" with the cause swallowed to a
    // console.warn. The group could never render, so it never fired.
    //
    // #762 separately proposes replacing this group with the `family_providers`
    // table, which is where providers actually live. That is still true and
    // still owned by #762; this only stops the group being unreachable by
    // construction in the meantime.
    const providers = shownFacts.filter((f) => f.category === "provider_ref" && !used.has(f));
    providers.forEach((f) => used.add(f));
    if (providers.length) {
      out.push({
        id: "provider_ref",
        title: "Providers",
        accent: PROVIDER_ACCENT,
        category: "provider_ref",
        subject: null,
        linkToMyKids: false,
        facts: providers,
      });
    }

    // Any remaining categories, grouped by category label.
    const remaining = shownFacts.filter((f) => !used.has(f));
    const byCategory = new Map<string, Fact[]>();
    for (const f of remaining) {
      const arr = byCategory.get(f.category) || [];
      arr.push(f);
      byCategory.set(f.category, arr);
    }
    for (const [category, arr] of byCategory) {
      out.push({
        id: `cat::${category}`,
        title: humanizeKey(category),
        accent: HOUSEHOLD_ACCENT,
        category,
        subject: null,
        linkToMyKids: false,
        facts: arr,
      });
    }

    // ── #762: Providers, from `family_providers` ────────────────────────────
    //
    // A SECOND Providers group, deliberately, rather than merging into the
    // `provider_ref` one above. Those are family_context rows kin-chat wrote
    // and they are editable as text; these are table rows with their own
    // columns and their own archive path. Merging them would put two things
    // that behave differently behind one set of controls.
    // ⚠️ Only rows that actually carry a name. `humanizeKey` splits `fact.key`
    // unguarded, so a row with no `type` throws INSIDE the render and blanks
    // this entire section — every group, not just Providers. `type` and
    // `provider_name` are NOT NULL in the schema, so this should be
    // unreachable; the cost of being wrong is the whole hub, and the cost of
    // the guard is one filter.
    const usableProviders = shownProviderRows.filter(
      (r) => typeof r?.provider_name === "string" && r.provider_name.trim().length > 0,
    );
    if (usableProviders.length > 0) {
      out.push({
        id: "family_providers",
        title: "Providers",
        accent: PROVIDER_ACCENT,
        subject: null,
        linkToMyKids: false,
        // null: these are not family_context rows, so there is no legal
        // category an add from here could insert. See Group.category.
        category: null,
        facts: usableProviders.map((r) => ({
          id: `provider:${r.id}`,
          origin: "family_providers" as const,
          category: PROVIDER_FACT_CATEGORY,
          subject: r.linked_kid_name,
          // The type IS the label here ("pediatrician", "dentist"), so the row
          // reads "Pediatrician — Dr Ruiz" the way the other groups read
          // "Birthday — Mar 12".
          key: typeof r.type === "string" && r.type ? r.type : "provider",
          value: r.linked_kid_name ? `${r.provider_name} (${r.linked_kid_name})` : r.provider_name,
          source: null,
          confidence: null,
          metadata: null,
          learned_at: r.last_visit,
        })),
      });
    }

    // ── #762: Focus areas, from `user_preferences.focus_areas` ──────────────
    //
    // ⚠️ THESE ARE STABLE KEYS, NOT TEXT. focusAreas.ts documents it and
    //    `orderSectionsByFocus` consumes them; `FOCUS_AREA_TYPES[key] ?? []`
    //    means a key that matches nothing fails SILENTLY — the focus area just
    //    stops doing anything, with no error anywhere. So they render by their
    //    catalog label and are deletable, and free-text editing is refused in
    //    startEdit. #762 asks for "the same view/edit affordances as the other
    //    groups"; for this group that would be a silent corruption, so it gets
    //    view and delete only. Adding stays where it is, in the six-pill card.
    const storedFocus = shownFocusAreas;
    if (storedFocus.length > 0) {
      out.push({
        id: "focus_areas",
        title: "What Kin keeps on top of",
        accent: FOCUS_ACCENT,
        category: null,
        subject: null,
        linkToMyKids: false,
        facts: storedFocus.map((key) => {
          const area = FOCUS_AREAS.find((a) => a.key === key);
          return {
            id: `focus:${key}`,
            origin: "focus_area" as const,
            category: FOCUS_FACT_CATEGORY,
            subject: null,
            key: "focus",
            // An unrecognised stored key still renders, as itself. Hiding it
            // would make a value that is really in the column invisible in the
            // one screen meant to show her what Kin knows.
            value: area ? `${area.emoji} ${area.label}` : key,
            source: null,
            confidence: null,
            metadata: null,
            learned_at: null,
          };
        }),
      });
    }

    // Render groups that hold facts, plus any that carry an invitation to add
    // the first one (today: "You" only, and only for accounts that can add).
    return out.filter((g) => g.facts.length > 0 || g.emptyCopy);
  }, [shownFacts, kidNames, kidColors, canEdit, shownProviderRows, shownFocusAreas]);

  // #1242 — what actually renders: guesses first, capped per group, filtered.
  const learnedFactTotal = useMemo(() => countFacts(groups), [groups]);
  const pinnedFactIds = useMemo(
    () => new Set([lastAddedFactId, targetFactId].filter((id): id is string => !!id)),
    [lastAddedFactId, targetFactId],
  );
  const hardPinnedFactIds = useMemo(() => new Set(editingId ? [editingId] : []), [editingId]);
  const groupViews = useMemo(
    () =>
      buildLearnedView(groups, {
        query: factFilter,
        expandedGroupIds,
        collapsedGroupIds,
        hardPinnedFactIds,
        pinnedFactIds,
        isGuess: isUnconfirmedGuess,
        // What she reads on the row: the label, the formatted value, and who it's about.
        searchText: (f) => `${humanizeKey(f.key)} ${formatValue(f.key, f.value)} ${f.subject ?? ""}`,
      }),
    [groups, factFilter, expandedGroupIds, collapsedGroupIds, hardPinnedFactIds, pinnedFactIds],
  );
  const setGroupExpanded = useCallback((groupId: string, open: boolean) => {
    const toggle = (prev: Set<string>, add: boolean) => {
      const next = new Set(prev);
      if (add) next.add(groupId);
      else next.delete(groupId);
      return next;
    };
    setExpandedGroupIds((prev) => toggle(prev, open));
    setCollapsedGroupIds((prev) => toggle(prev, !open));
  }, []);

  // --- Pro gate helper -------------------------------------------------------
  const requirePro = useCallback((): boolean => {
    if (canEdit) return true;
    // Issue #50: same copy, now with a "See Pro" action so the gate hit isn't
    // a dead end. Gate evaluation (canEdit) is untouched.
    //
    // ⚠️ #763 REWROTE THE BODY, and the old line is the reason. It read "For
    // now you can view and delete what I remember. Once you're Pro I'll help
    // you add and edit it too." — a promise of free view-and-delete access
    // that #763 removes. Leaving it would have had Kin describing the
    // behaviour this screen had yesterday, in a toast fired BY the new
    // behaviour.
    //
    // The replacement says what is actually free rather than what is Pro, and
    // it deliberately matches the locked empty-state card a few hundred lines
    // below ("Right now I keep track of your crew's names and birthdays up in
    // My Kids…") — one account of the carve-out, in two places, rather than
    // two wordings a reader has to reconcile. No new pricing or caps: brand
    // guardrail, Pro claims are Kasandra's.
    proUpsellToast(
      "This one's a Kin Pro thing 💜",
      "Names and birthdays live in My Kids and they're yours for free. The rest — allergies, doctors, sizes, all the little things — comes with Kin Pro."
    );
    return false;
  }, [canEdit, proUpsellToast]);

  // --- Delete (soft archive) + Undo -----------------------------------------
  const restoreFact = useCallback(
    async (fact: Fact) => {
      if (!user) return;
      // Optimistic restore.
      setFacts((prev) => (prev.some((f) => f.id === fact.id) ? prev : [...prev, fact]));
      try {
        // ⚠️ #1227 task 4 — the UNDO path, and the most expensive place in this
        // file to get it wrong. An RLS-refused update returns 200 with
        // error === null and zero rows, so `if (error)` alone could not see a
        // failed undo: the fact stayed on screen from the optimistic setFacts
        // above, she believed her delete was reversed, and the row was still
        // archived. CLAUDE.md names this exact shape — "an Undo whose failure
        // left her list permanently gone with no message".
        const { data, error } = await supabase
          .from("family_context")
          .update({ archived: false })
          .eq("id", fact.id)
          .eq("user_id", user.id)
          .select("id");
        if (error || !data || data.length === 0) {
          console.error("[family-profile] restore did not land", error);
          setFacts((prev) => prev.filter((f) => f.id !== fact.id));
          toast.error("Couldn't undo. Try again?");
        }
      } catch (e) {
        console.error("[family-profile] restore threw", e);
        setFacts((prev) => prev.filter((f) => f.id !== fact.id));
        // The catch previously rolled back in silence, so a thrown undo looked
        // identical to one she never tapped.
        toast.error("Couldn't undo. Try again?");
      }
    },
    [user]
  );

  /**
   * #762 — a provider lives in `family_providers`, so "delete" is an archive
   * on that table. Read-back verified like every other write in this file: an
   * RLS refusal resolves 200 with zero rows and no error, so the optimistic
   * removal would otherwise leave her believing Kin forgot a pediatrician it
   * still knows.
   */
  const archiveProvider = useCallback(
    async (fact: Fact) => {
      if (!user) return;
      const rowId = fact.id.slice("provider:".length);
      const previous = providerRows;
      setProviderRows((prev) => prev.filter((r) => r.id !== rowId));
      try {
        const { data, error } = await supabase
          .from("family_providers")
          .update({ archived: true })
          .eq("id", rowId)
          .eq("user_id", user.id)
          .select("id");
        if (error || !data || data.length === 0) {
          console.error("[family-profile] provider archive did not land", error);
          setProviderRows(previous);
          toast.error("Couldn't delete that. Try again?");
          return;
        }
      } catch (e) {
        console.error("[family-profile] provider archive threw", e);
        setProviderRows(previous);
        toast.error("Couldn't delete that. Try again?");
        return;
      }
      toast.success("Deleted 💜");
    },
    [user, providerRows],
  );

  /**
   * #762 — a focus area is one entry in `user_preferences.focus_areas`.
   *
   * ⚠️ Writes [] rather than null when the last one goes. null means NEVER
   * ASKED and makes consumers fall back to their own default order; an empty
   * array means she asked for none. Collapsing those would silently restore a
   * default she just cleared.
   */
  const removeFocusArea = useCallback(
    async (fact: Fact) => {
      const key = fact.id.slice("focus:".length);
      const current = Array.isArray(prefs.focus_areas) ? prefs.focus_areas : [];
      const next = current.filter((k) => k !== key);
      if (next.length === current.length) return;
      // CHECKED, not caught (#545): updatePreferences RESOLVES with the error
      // rather than throwing, so a try/catch around it could never fire.
      const error = await updatePreferences({ focus_areas: next });
      if (error) {
        console.error("[family-profile] focus area removal failed", error);
        toast.error("Couldn't delete that. Try again?");
        return;
      }
      toast.success("Deleted 💜");
    },
    [prefs.focus_areas, updatePreferences],
  );

  const archiveFact = useCallback(
    async (fact: Fact, restoreToast = true) => {
      if (!user) return;
      // #762 — dispatch by origin BEFORE anything else. Doing it here rather
      // than at the four call sites in the render means the rendering, and
      // every existing family_context path below, is untouched.
      if (fact.origin === "family_providers") return archiveProvider(fact);
      if (fact.origin === "focus_area") return removeFocusArea(fact);
      // #763 — delete follows the same gate as view: a free account must not
      // be able to delete a fact she can no longer see.
      //
      // ⚠️ Not dead code, and not belt-and-braces theatre. `targetFactId`
      // (#667/#666) auto-opens a fact BY ID from a deep link, `archiveFact` is
      // also called from the undo path, and the id in either case comes from
      // outside this render. A guard that lives only in the group builder is a
      // guard that a second entry point walks around — which is how #1214's
      // silent no-op delete happened one table over.
      if (!canSeeProFacts && !isFreeTierFact(fact)) {
        requirePro();
        return;
      }
      // Optimistic remove.
      setFacts((prev) => prev.filter((f) => f.id !== fact.id));
      try {
        // ⚠️ #1227 task 4 — a silently-failed DELETE is the worst of the three
        // in this file. The optimistic setFacts removes it from her screen, so
        // she believes Kin has forgotten it; the row is still `archived = false`
        // and kin-chat goes on reading it. That is the #1214 shape — a delete
        // that reports success and no-ops — pointed at a single fact.
        const { data, error } = await supabase
          .from("family_context")
          .update({ archived: true })
          .eq("id", fact.id)
          .eq("user_id", user.id)
          .select("id");
        if (error || !data || data.length === 0) {
          console.error("[family-profile] archive did not land", error);
          setFacts((prev) => [...prev, fact]); // roll back
          toast.error("Couldn't delete that. Try again?");
          return;
        }
      } catch (e) {
        // console.ERROR, not warn — #1227 task 2 wired Sentry to capture
        // console.error only. A thrown write logged at warn level is exactly
        // the silent failure this issue exists to close, one level out from the
        // returned-error branch above.
        console.error("[family-profile] archive threw", e);
        setFacts((prev) => [...prev, fact]);
        toast.error("Couldn't delete that. Try again?");
        return;
      }

      if (restoreToast) {
        toast(`Removed "${humanizeKey(fact.key)}"`, {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: () => void restoreFact(fact),
          },
        });
      }
    },
    [user, restoreFact, archiveProvider, removeFocusArea, canSeeProFacts, requirePro]
  );

  // --- Confirm / reject a chat-learned guess ---------------------------------
  const confirmGuess = useCallback(
    async (fact: Fact) => {
      if (!user) return;
      const nextMeta = { ...(fact.metadata || {}), confirmed: true };
      setFacts((prev) =>
        prev.map((f) => (f.id === fact.id ? { ...f, metadata: nextMeta, confidence: "high" } : f))
      );
      try {
        // ⚠️ #1227 task 4 — `.select("id")` is not decoration. supabase-js
        // RESOLVES on an RLS refusal: HTTP 200, error === null, zero rows. So
        // `if (!error) toast.success(...)` confirmed a write that may never
        // have happened, and a false confirmation is worse than silence —
        // silence makes her check, a confirmation makes her stop checking.
        //
        // This was the last family_context write in this file still shaped that
        // way; the add and edit paths already read back. Found by the test in
        // familyMemoryFailedReadStates.test.ts rather than by reading.
        const { data, error } = await supabase
          .from("family_context")
          .update({ metadata: nextMeta, confidence: "high", updated_at: new Date().toISOString() })
          .eq("id", fact.id)
          .eq("user_id", user.id)
          .select("id");
        if (error || !data || data.length === 0) {
          console.error("[family-profile] confirm did not land", error);
          // Put the row back the way it was — the optimistic setFacts above
          // already showed it as confirmed.
          setFacts((prev) =>
            prev.map((f) => (f.id === fact.id ? fact : f))
          );
          toast.error("Couldn't save that. Try again?");
        } else {
          toast.success("Got it — thanks for confirming 💜");
        }
      } catch (e) {
        console.error("[family-profile] confirm threw", e);
        setFacts((prev) => prev.map((f) => (f.id === fact.id ? fact : f)));
        toast.error("Couldn't save that. Try again?");
      }
    },
    [user]
  );

  // --- Inline edit -----------------------------------------------------------
  const startEdit = (fact: Fact) => {
    if (!requirePro()) return;
    // ⚠️ #762 — a focus area is a STABLE KEY, not text. Editing it as free
    // text writes a value that matches nothing in FOCUS_AREAS, and
    // `FOCUS_AREA_TYPES[key] ?? []` means that fails SILENTLY: the focus area
    // simply stops ordering anything, with no error to notice. So this group
    // is view-and-delete; changing what Kin keeps on top of belongs in the
    // six-pill card that owns the catalog.
    if (fact.origin === "focus_area") {
      toast("Change these in Pro setup — tap What should Kin keep on top of?");
      return;
    }
    setAddingFor(null);
    setEditingId(fact.id);
    setEditDraft(fact.value);
    // #789 — only an activity Today is silently skipping gets the picker, and
    // whether it shows is decided ONCE here, when the row opens. Re-deciding
    // it from the live draft would make the picker vanish mid-edit the moment
    // she typed a weekday into the text field.
    if (isUnresolvedActivityFact(fact)) {
      setScheduleFactId(fact.id);
      setScheduleDays([]);
      setScheduleTime(parseActivityRecurrence(fact.value).time ?? "");
    } else {
      setScheduleFactId(null);
    }
  };

  // #667/#666 — auto-open targetFactId once, after facts finish loading. The
  // ref guard is what makes it "once": without it, editingId briefly stealing
  // focus back every render would keep reopening the same row every time
  // facts re-fetches. A free-tier tap on a birthday/activity band still
  // lands here and still hits requirePro() inside startEdit like any other
  // edit attempt — that's the existing edit gate, not something new.
  const autoOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loaded || !targetFactId || autoOpenedRef.current === targetFactId) return;
    // #763 — the VISIBLE set. A deep link to a Pro fact on a free account must
    // not open an editor over a row that is not on the screen; `startEdit`'s
    // requirePro() already refuses the edit, but opening an editor for an
    // invisible fact would put its VALUE in the draft field, which is the
    // thing #763 is removing.
    const fact = shownFacts.find((f) => f.id === targetFactId);
    if (!fact) return;
    autoOpenedRef.current = targetFactId;
    startEdit(fact);
  }, [loaded, targetFactId, shownFacts]);

  /**
   * #762 — editing a provider renames it in `family_providers`.
   *
   * Only `provider_name` is editable here. The row also carries type,
   * linked_kid_name, last_visit and cadence_months, and this hub shows one
   * text line per fact — a single field is what that affordance can honestly
   * write. The rest stay owned by the Pro setup flow.
   */
  const saveProviderEdit = useCallback(
    async (fact: Fact) => {
      if (!user) return;
      const next = editDraft.trim();
      const rowId = fact.id.slice("provider:".length);
      const row = providerRows.find((r) => r.id === rowId);
      if (!row || !next || next === row.provider_name) {
        closeEdit();
        return;
      }
      const previous = providerRows;
      setProviderRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, provider_name: next } : r)),
      );
      closeEdit();
      try {
        const { data, error } = await supabase
          .from("family_providers")
          .update({ provider_name: next })
          .eq("id", rowId)
          .eq("user_id", user.id)
          .select("id");
        if (error || !data || data.length === 0) {
          console.error("[family-profile] provider edit did not land", error);
          setProviderRows(previous);
          toast.error("Couldn't save that. Try again?");
          return;
        }
      } catch (e) {
        console.error("[family-profile] provider edit threw", e);
        setProviderRows(previous);
        toast.error("Couldn't save that. Try again?");
        return;
      }
      toast.success("Saved 💜");
    },
    [user, editDraft, providerRows, closeEdit],
  );

  const saveEdit = useCallback(
    async (fact: Fact) => {
      if (!user) return;
      // #762 — a provider is a row in another table. Dispatch before any of the
      // family_context machinery below touches it.
      if (fact.origin === "family_providers") return saveProviderEdit(fact);
      const typed = editDraft.trim();
      // #789 — a picked day folds into the same `value` column this row has
      // always written. One string, no second copy to drift (see the
      // deep_link_url note in CLAUDE.md).
      const scheduling = scheduleFactId === fact.id && scheduleDays.length > 0;
      const next = scheduling
        ? applyActivitySchedule(typed, scheduleDays, scheduleTime || null)
        : typed;
      if (!next || next === fact.value) {
        closeEdit();
        return;
      }
      const nextMeta = { ...(fact.metadata || {}), previous_value: fact.value };
      // Field-scoped snapshot for rollback — only the keys this write touches,
      // taken into a plain local BEFORE the optimistic set.
      const before = {
        value: fact.value,
        source: fact.source,
        confidence: fact.confidence,
        metadata: fact.metadata,
      };
      setFacts((prev) =>
        prev.map((f) =>
          f.id === fact.id ? { ...f, value: next, source: "manual", confidence: "high", metadata: nextMeta } : f
        )
      );
      closeEdit();
      try {
        // House rule 16: this path now confirms a day will show up on her week,
        // so the write has to be read back. An RLS-refused update returns 200
        // with error === null and no rows — `.select()` is the only way to see it.
        const { data, error } = await supabase
          .from("family_context")
          .update({
            value: next,
            source: "manual",
            confidence: "high",
            metadata: nextMeta,
            updated_at: new Date().toISOString(),
          })
          .eq("id", fact.id)
          .eq("user_id", user.id)
          .select("id");
        if (error) throw error;
        assertWriteLanded(data, "fact");
        toast.success(scheduling ? "Got it — that'll show up on your week 💜" : "Updated 💜");
      } catch (e) {
        console.error("[family-profile] edit failed", e);
        setFacts((prev) => prev.map((f) => (f.id === fact.id ? { ...f, ...before } : f)));
        toast.error("Couldn't save. Try again?");
      }
    },
    [user, editDraft, scheduleFactId, scheduleDays, scheduleTime, closeEdit, saveProviderEdit]
  );

  // --- Inline add ------------------------------------------------------------
  const startAdd = (group: Group) => {
    if (!requirePro()) return;
    closeEdit();
    setAddKey("");
    setAddValue("");
    setAddingFor(group.id);
  };

  const saveAdd = useCallback(
    async (group: Group) => {
      if (!user) return;
      // ⚠️ #762 — a group with no category is not backed by family_context, so
      // there is nothing legal to insert. #1241 was this exact shape: an add
      // from a group whose category is not in family_context_category_check
      // takes a 23514 that the UI swallows to "Couldn't add that".
      if (!group.category) {
        setAddingFor(null);
        return;
      }
      const key = addKey.trim().toLowerCase().replace(/\s+/g, "_");
      const value = addValue.trim();
      if (!key || !value) {
        setAddingFor(null);
        return;
      }
      // #1481 — through the RPC, not a bare .insert().
      //
      // `key` is FREE-FORM text she types (normalised just above), and
      // family_context_fact_uidx is unique on
      // (user_id, category, coalesce(subject,''), key) where archived = false
      // for every key outside the six #747 multi-valued ones. So typing a label
      // she has already used for that person — "nickname", "shoe_size",
      // "bedtime" — was a 23505 that surfaced as "Couldn't add that. Try
      // again?", advice that could never work: retrying types the same key and
      // collides again, forever. The fact she wanted to change sat right there
      // and the UI would not let her change it by adding.
      //
      // NOT fixable with .upsert({ onConflict }) — expression index
      // (coalesce(subject,'')) with a partial WHERE, so PostgREST cannot infer
      // it and Postgres answers 42P10. See src/lib/rememberFamilyFact.ts.
      //
      // upsert_family_fact does the right thing for both shapes: it overwrites
      // the one live row for a single-valued key (keeping the old text in
      // metadata.previous_value) and ADDS alongside for a #747 multi-valued one,
      // which is what "add" means for an allergy or an activity.
      const result = await rememberFamilyFact(
        { category: group.category, subject: group.subject, key, value },
        "manual",
        "high",
      );

      // `ok` is the only honest success signal: the RPC reports refusals
      // (rejected_category, rejected_medical, rejected_rate_limit, …) as a
      // RETURNED action with no Postgres error, so `if (error)` alone would
      // print "Added 💜" over a write that never landed (house rule 16). #1241
      // was this same toast swallowing a 23514 from the category check.
      if (!result.ok) {
        // #1857 — "unverified" (lost response, read-back could not answer) is
        // titled apart from a real failure; familyFactAddErrorCopy says
        // "couldn't confirm" for it rather than "couldn't add".
        reportError(`[family-profile] add ${isUnverifiedFactWrite(result) ? "unverified" : "failed"}`, Object.assign(
          new Error(`upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`),
          { code: result.action ?? "no_action_row", details: result.error },
        ), {
          tags: { area: "family-profile", writer: "add_fact" },
          extra: { code: result.action ?? "no_action_row", details: result.error, category: group.category, key },
        });
        // ⚠️ NOT result.error. That field's own type says "Model-facing,
        // already phrased for Kin to speak" — it is second-person instruction
        // to the realtime voice model ("Tell her that warmly, in your own
        // words, and don't retry"), and rendering it here showed a mom typing a
        // medication dose exactly that, verbatim. Caught in review before it
        // merged. familyFactAddErrorCopy maps the action to words for HER.
        toast.error(familyFactAddErrorCopy(result.action));
        setAddingFor(null);
        return;
      }

      // Read the row back rather than construct it. The list renders
      // learned_at, metadata and source, and the RPC owns all three — an
      // optimistic row would drift from what the database actually holds, and
      // for an `update` it would be a different row than the one on screen.
      //
      // Only the READ needs a try/catch. rememberFamilyFact never throws — it
      // returns every failure so a caller can decide (that is its documented
      // contract), which is why the guard above is an `if` and not a catch.
      // A network-level rejection here, though, would skip setAddingFor(null)
      // and leave the add row open with no feedback at all.
      // #1242 — pin by the id the RPC returned, BEFORE the read-back. If the
      // read-back fails the row arrives on the next load, and the pin keeps it
      // above the cap then too (Copilot on #1614).
      if (result.factId) setLastAddedFactId(result.factId);
      try {
        if (result.factId) {
          const { data, error: readErr } = await supabase
            .from("family_context")
            .select("id, category, subject, key, value, source, confidence, metadata, learned_at")
            .eq("id", result.factId)
            .maybeSingle();
          if (readErr || !data) {
          // The write LANDED; only the read-back failed. Say nothing
          // misleading and let the next load show it rather than claiming a
          // failure that did not happen.
            reportError("[family-profile] add read-back failed", readErr ?? new Error("no row"), {
              tags: { area: "family-profile", writer: "add_fact" },
              extra: { factId: result.factId },
            });
          } else {
            // Replace-or-append by id: `update` and `duplicate` both return an
            // EXISTING row, which is already in the list.
            setFacts((prev) => {
              const row = data as Fact;
              return prev.some((f) => f.id === row.id)
                ? prev.map((f) => (f.id === row.id ? row : f))
                : [...prev, row];
            });

          }
        }
      } catch (e) {
        // The write landed; the read-back threw. Same reasoning as above: do
        // not turn a successful save into an error message she cannot act on.
        reportError("[family-profile] add read-back threw", e, {
          tags: { area: "family-profile", writer: "add_fact" },
          extra: { factId: result.factId },
        });
      }

      // Say what actually happened. "Added 💜" over an overwrite would be a
      // small lie about her own data, and the whole point of #1481 is that she
      // could not tell what the app had done with what she typed.
      toast.success(
        result.action === "update"
          ? "Updated 💜"
          : result.action === "duplicate"
            ? "Kin already had that 💜"
            : "Added 💜",
      );
      setAddingFor(null);
    },
    [user, addKey, addValue]
  );

  const dismissSetup = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissedSetup(true);
  };

  /**
   * ⚠️ NOT just `family_context`. This gates the empty/locked state below, and
   * #762 gave the hub two more sources — so a mom with a pediatrician on file
   * and nothing else would have been told "Kin hasn't learned much yet 🌱"
   * while her Providers group sat one branch away, unrendered. That is exactly
   * the absence-reads-as-emptiness bug #1210 fixed for the read path, one
   * branch over. (Copilot review, #1312.)
   *
   * Mirrors the group builders: a provider counts only if it has a name (a
   * nameless row renders nothing), and focus areas count from the stored array.
   */
  const hasFacts =
    shownFacts.length > 0 ||
    shownProviderRows.some((r) => typeof r?.provider_name === "string" && r.provider_name.trim().length > 0) ||
    shownFocusAreas.length > 0;

  return (
    <section className="mt-10">
      <h2
        className="font-body uppercase font-semibold mb-2"
        style={{ fontSize: "11px", letterSpacing: "0.5px", color: "#54525C" }}
      >
        Family Profile
      </h2>
      <div className="h-px mb-3" style={{ backgroundColor: "#E8E8F4" }} />

      {/* #763 — `!tierResolved` waits with the facts fetch rather than
          rendering the free view and then swapping it for the Pro one. See
          the flag's definition: this is "not known yet", not "free". */}
      {!loaded || !tierResolved ? (
        <div className="bg-white" style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}>
          <p className="font-body text-sm italic" style={{ color: "#8B8B9E" }}>
            Loading…
          </p>
        </div>
      ) : loadFailed ? (
        // #1210 — the read failed. This MUST NOT fall through to the empty
        // state below: "Kin hasn't learned much yet" over a broken query is
        // how this bug hid for seven weeks. Her facts are safe; we just
        // couldn't fetch them, and the copy has to say that and nothing else.
        <div
          data-testid="family-profile-load-error"
          className="bg-white"
          style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}
        >
          <p className="font-body font-semibold" style={{ fontSize: 15, color: "#1C1A3E" }}>
            Hmm — Kin couldn't load this just now
          </p>
          <p className="font-body text-sm mt-1.5" style={{ color: "#54525C", lineHeight: 1.5 }}>
            Everything Kin remembered is still safe. This is on our end, not
            yours. Give it a moment and try again.
          </p>
          <button
            onClick={() => {
              setLoaded(false);
              setLoadFailed(false);
              void loadFacts();
            }}
            className="font-body font-semibold mt-2.5"
            style={{
              fontSize: 13,
              color: "#5050C8",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      ) : !hasFacts ? (
        // Empty / skipped state: a dismissible resurfacing card (never a modal).
        // Tier is checked BEFORE dismiss state — free tier always sees the
        // locked Aug-5 copy regardless of whether they've dismissed the card;
        // only Pro tier's dismissed view falls back to the passive-capture copy.
        !canEdit ? (
          <div className="relative bg-white" style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}>
            <p className="font-body font-semibold" style={{ fontSize: 15, color: "#1C1A3E" }}>
              This one's a Kin Pro thing 💜
            </p>
            <p className="font-body text-sm mt-1.5" style={{ color: "#54525C", lineHeight: 1.5 }}>
              Right now I keep track of your crew's names and birthdays up in{" "}
              <strong>My Kids</strong>. Once you're Pro, I'll remember the rest
              too: allergies, doctors, teachers, which kid refuses to wear jeans. You
              tell me once in chat, and it lives here.
            </p>
            {/* Issue #50: this locked card used to dead-end. */}
            <div className="mt-2.5">
              <ProUpsellLink label="See what's in Kin Pro 💜" fontSize={13} />
            </div>
          </div>
        ) : dismissedSetup ? (
          <div className="bg-white" style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}>
            <p className="font-body text-sm" style={{ color: "#54525C", lineHeight: 1.5 }}>
              Nothing here yet. As you chat, Kin will remember the little things —
              birthdays, allergies, who's who — and they'll show up here for you to
              check and tidy anytime. 💜
            </p>
          </div>
        ) : (
          <div
            className="relative bg-white"
            style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}
          >
            <button
              onClick={dismissSetup}
              aria-label="Dismiss"
              className="absolute flex items-center justify-center rounded-full hover:bg-muted"
              style={{ top: 10, right: 10, width: 26, height: 26, color: "#8B8B9E" }}
            >
              <X size={15} />
            </button>
            <p className="font-body font-semibold" style={{ fontSize: 15, color: "#1C1A3E", marginRight: 24 }}>
              Kin hasn't learned much yet 🌱
            </p>
            <p className="font-body text-sm mt-1.5" style={{ color: "#54525C", lineHeight: 1.5 }}>
              Tell Kin about your crew in chat — "Josh's allergic to peanuts",
              "we go to Dr. Lee" — and Kin remembers it here. You can add your
              kids and their birthdays in <strong>My Kids</strong> above to get
              started.
            </p>
          </div>
        )
      ) : (
        <div className="flex flex-col" style={{ gap: 14 }}>
          {/* #762 — a failed PROVIDER read is not an absence of providers.
              Same rule as #1210's loadFailed above, scoped to its own source: if
              this query broke, saying nothing would read as "you have no
              pediatrician on file", which is a different and wrong statement. */}
          {providersFailed && (
            <p
              data-testid="family-profile-providers-error"
              className="font-body"
              style={{ fontSize: 13, color: "#6B6B8A", marginBottom: 10 }}
            >
              Kin couldn&rsquo;t load your providers just now — they&rsquo;re still saved.
            </p>
          )}
          {/* #1242 — filter. Only once the list is long enough to need one, and
              kept while she has typed something so it can't vanish mid-search.
              Enter means nothing here, so no Enter handler; autocorrect off so
              iOS doesn't rewrite a name she's searching for. */}
          {(learnedFactTotal >= LEARNED_FILTER_MIN_FACTS || factFilter) && (
            <div className="flex items-center" style={{ gap: 8 }}>
              <input
                type="search"
                value={factFilter}
                onChange={(e) => setFactFilter(e.target.value)}
                placeholder="Find something Kin learned…"
                aria-label="Find something Kin learned"
                data-testid="family-profile-filter"
                enterKeyHint="search"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                className="font-body bg-white flex-1 min-w-0"
                style={{
                  fontSize: 16,
                  color: "#1C1A3E",
                  border: "1.5px solid #C8C8F4",
                  borderRadius: 12,
                  padding: "9px 12px",
                  outline: "none",
                }}
              />
              {factFilter && (
                <button
                  onClick={() => setFactFilter("")}
                  aria-label="Clear search"
                  data-testid="family-profile-filter-clear"
                  className="flex items-center justify-center rounded-full"
                  style={{ width: 44, height: 44, color: "#54525C" }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
          {factFilter.trim() && groupViews.length === 0 && (
            <p data-testid="family-profile-filter-empty" className="font-body text-sm" style={{ color: "#54525C" }}>
              Nothing Kin learned matches &ldquo;{factFilter.trim()}&rdquo;.
            </p>
          )}
          {groupViews.map(({ group, shown, hiddenCount, total, expanded }) => (
            <div
              key={group.id}
              className="bg-white"
              style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}
            >
              {/* Group header */}
              <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: group.accent,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <h3 className="font-body font-semibold" style={{ fontSize: 15, color: "#1C1A3E" }}>
                  {group.title}
                </h3>
              </div>

              {group.linkToMyKids && (
                <p className="font-body" style={{ fontSize: 11, color: "#8B8B9E", marginBottom: 10 }}>
                  Name and color live in My Kids ↑
                </p>
              )}

              {/* An empty group that carries an invitation (#1241 — "You").
                  The "Add a fact" button below is the action; this is only the
                  reason to press it, so the card isn't a bare heading. */}
              {group.facts.length === 0 && group.emptyCopy && (
                <p
                  data-testid="family-profile-group-empty"
                  className="font-body text-sm"
                  style={{ color: "#54525C", lineHeight: 1.5 }}
                >
                  {group.emptyCopy}
                </p>
              )}

              {/* Facts */}
              <div className="flex flex-col" style={{ gap: 8 }}>
                {shown.map((fact) => {
                  const isEditing = editingId === fact.id;
                  const meta = sourceMeta(fact.source);
                  const guess = isUnconfirmedGuess(fact);
                  // #789 — stored, but Today's activity layer can't place it.
                  // Gated on canEdit for accuracy, not upsell: the week band
                  // this promises is itself Pro-gated (TodayTab's
                  // useWeeklyActivities(..., isProTier)), so on free tier the
                  // marker would promise something that wasn't going to happen
                  // and offer a fix she can't apply.
                  const needsDay = canEdit && isUnresolvedActivityFact(fact);
                  const showPicker = isEditing && scheduleFactId === fact.id;
                  return (
                    <div
                      key={fact.id}
                      className="flex flex-col"
                      style={{ gap: 6, padding: "10px 12px", backgroundColor: "#F8F8FF", borderRadius: 10 }}
                    >
                      <div className="flex items-start" style={{ gap: 10 }}>
                        <div className="flex-1 min-w-0">
                          <p className="font-body" style={{ fontSize: 11, color: "#8B8B9E", marginBottom: 1 }}>
                            {humanizeKey(fact.key)}
                          </p>
                          {isEditing ? (
                            usesTextarea(fact.key, editDraft) ? (
                              <textarea
                                ref={editInputRef as React.RefObject<HTMLTextAreaElement>}
                                value={editDraft}
                                rows={1}
                                onChange={(e) => {
                                  setEditDraft(e.target.value);
                                  // Grow with the content. Reset to auto first
                                  // or scrollHeight only ever ratchets upward
                                  // and the box never shrinks on delete.
                                  e.currentTarget.style.height = "auto";
                                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                }}
                                onKeyDown={(e) => {
                                  // Escape cancels, exactly as the input does.
                                  if (e.key === "Escape") closeEdit();
                                  // ⚠️ Enter does NOT save here, unlike the
                                  // input. These values legitimately contain
                                  // line breaks — the Babysitter sheet writes
                                  // bedtime_routine and medications from
                                  // multi-line textareas — and saving on Enter
                                  // would make a newline impossible to type,
                                  // which is most of why a textarea is right
                                  // for this field. Cmd/Ctrl+Enter saves for
                                  // anyone who reaches for a keyboard save;
                                  // the ✓ button is the primary path and is
                                  // unchanged.
                                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                    e.preventDefault();
                                    saveEdit(fact);
                                  }
                                }}
                                className="font-body bg-white w-full"
                                style={{
                                  fontSize: 14,
                                  color: "#1C1A3E",
                                  border: "1px solid #C8C8F4",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  outline: "none",
                                  resize: "none",
                                  overflow: "hidden",
                                  lineHeight: 1.4,
                                  boxSizing: "border-box",
                                }}
                              />
                            ) : (
                              <input
                                ref={editInputRef as React.RefObject<HTMLInputElement>}
                                type={fact.key === "birthdate" ? "date" : "text"}
                                value={editDraft}
                                max={fact.key === "birthdate" ? new Date().toISOString().slice(0, 10) : undefined}
                                onChange={(e) => setEditDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit(fact);
                                  if (e.key === "Escape") closeEdit();
                                }}
                                className="font-body bg-white w-full"
                                style={{
                                  fontSize: 14,
                                  color: "#1C1A3E",
                                  border: "1px solid #C8C8F4",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  outline: "none",
                                }}
                              />
                            )
                          ) : (
                            <p className="font-body" style={{ fontSize: 14, color: "#1C1A3E", lineHeight: 1.4 }}>
                              {formatValue(fact.key, fact.value)}
                            </p>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="flex items-center flex-shrink-0" style={{ gap: 2 }}>
                            <button
                              onClick={() => saveEdit(fact)}
                              aria-label="Save"
                              className="flex items-center justify-center rounded-full"
                              style={{ width: 28, height: 28, color: "#5050C8" }}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={closeEdit}
                              aria-label="Cancel"
                              className="flex items-center justify-center rounded-full"
                              style={{ width: 28, height: 28, color: "#54525C" }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center flex-shrink-0" style={{ gap: 2 }}>
                            <button
                              onClick={() => startEdit(fact)}
                              aria-label={`Edit ${humanizeKey(fact.key)}`}
                              className="flex items-center justify-center rounded-full hover:bg-white"
                              style={{ width: 28, height: 28, color: "#54525C" }}
                            >
                              {canEdit ? <Pencil size={14} /> : <Lock size={13} />}
                            </button>
                            <button
                              onClick={() => archiveFact(fact)}
                              aria-label={`Delete ${humanizeKey(fact.key)}`}
                              className="flex items-center justify-center rounded-full hover:bg-white"
                              style={{ width: 28, height: 28, color: "#54525C" }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Source pill + learned date */}
                      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                        <span
                          className="font-body font-semibold"
                          style={{
                            fontSize: 10,
                            backgroundColor: meta.bg,
                            color: meta.color,
                            borderRadius: 999,
                            padding: "2px 8px",
                          }}
                        >
                          {meta.label}
                        </span>
                        {formatLearnedDate(fact.learned_at) && (
                          <span className="font-body" style={{ fontSize: 10, color: "#9090A8" }}>
                            {formatLearnedDate(fact.learned_at)}
                          </span>
                        )}
                      </div>

                      {/* #789 — "Kin stored it but can't place it" marker.
                          Lives here and nowhere else: no notification, no
                          badge, no count. She only meets it when she's already
                          reading what Kin knows. */}
                      {needsDay && !isEditing && (
                        <div
                          data-kin-activity-needs-day
                          className="flex items-center flex-wrap"
                          style={{ gap: 8, marginTop: 2 }}
                        >
                          <span className="font-body" style={{ fontSize: 12, color: "#54525C", lineHeight: 1.4 }}>
                            Kin's got this but couldn't pin down a day — add one and it'll
                            show up on your week.
                          </span>
                          <button
                            onClick={() => startEdit(fact)}
                            className="rounded-full font-body font-semibold"
                            style={{
                              fontSize: 12,
                              color: "#5050C8",
                              border: "1px solid #C8C8F4",
                              background: "#FFFFFF",
                              padding: "3px 10px",
                            }}
                          >
                            Add a day
                          </button>
                        </div>
                      )}

                      {/* #789 — the fix, inline on the row she's already on.
                          Same Save/Cancel buttons above; this only supplies
                          what gets written to the same `value` column. */}
                      {showPicker && (
                        <div data-kin-activity-day-picker style={{ marginTop: 2 }}>
                          <p className="font-body" style={{ fontSize: 11, color: "#8B8B9E", marginBottom: 5 }}>
                            Which day?
                          </p>
                          <div className="flex flex-wrap" style={{ gap: 6 }}>
                            {WEEKDAY_LABELS.map((label, day) => {
                              const on = scheduleDays.includes(day);
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  aria-label={label}
                                  aria-pressed={on}
                                  onClick={() => toggleScheduleDay(day)}
                                  className="font-body font-semibold rounded-full"
                                  style={{
                                    fontSize: 12,
                                    padding: "4px 10px",
                                    border: `1px solid ${on ? "#5050C8" : "#C8C8F4"}`,
                                    background: on ? "#5050C8" : "#FFFFFF",
                                    color: on ? "#FFFFFF" : "#54525C",
                                  }}
                                >
                                  {label.slice(0, 3)}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center" style={{ gap: 8, marginTop: 8 }}>
                            <label
                              className="font-body"
                              htmlFor={`activity-time-${fact.id}`}
                              style={{ fontSize: 11, color: "#8B8B9E" }}
                            >
                              Time (optional)
                            </label>
                            <input
                              id={`activity-time-${fact.id}`}
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="font-body bg-white"
                              style={{
                                fontSize: 13,
                                color: "#1C1A3E",
                                border: "1px solid #C8C8F4",
                                borderRadius: 8,
                                padding: "4px 8px",
                                outline: "none",
                              }}
                            />
                          </div>
                          {scheduleDays.length > 0 && (
                            <p
                              className="font-body"
                              data-kin-activity-day-preview
                              style={{ fontSize: 11, color: "#8B8B9E", marginTop: 7, lineHeight: 1.4 }}
                            >
                              Kin will remember it as “
                              {applyActivitySchedule(editDraft.trim(), scheduleDays, scheduleTime || null)}”
                            </p>
                          )}
                        </div>
                      )}

                      {/* Chat-learned guess confirmation */}
                      {guess && !isEditing && (
                        <div
                          className="flex items-center flex-wrap"
                          style={{ gap: 8, marginTop: 2 }}
                        >
                          <span className="font-body" style={{ fontSize: 12, color: "#B23A7A" }}>
                            Kin guessed this — look right?
                          </span>
                          <div className="flex items-center" style={{ gap: 6 }}>
                            <button
                              onClick={() => confirmGuess(fact)}
                              aria-label="Yes, that's right"
                              className="rounded-full"
                              style={{
                                fontSize: 13,
                                border: "1px solid #C8C8F4",
                                background: "#FFFFFF",
                                padding: "2px 8px",
                              }}
                            >
                              ✅
                            </button>
                            <button
                              onClick={() => archiveFact(fact)}
                              aria-label="No, remove it"
                              className="rounded-full"
                              style={{
                                fontSize: 13,
                                border: "1px solid #C8C8F4",
                                background: "#FFFFFF",
                                padding: "2px 8px",
                              }}
                            >
                              ❌
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* #1242 — the cap never hides anything silently: the count is
                    on the button, and "Show fewer" puts it back. */}
                {hiddenCount > 0 && (
                  <button
                    onClick={() => setGroupExpanded(group.id, true)}
                    data-testid={`family-profile-show-all-${group.id}`}
                    className="font-body font-semibold hover:underline text-left"
                    style={{ fontSize: 13, color: "#5050C8", minHeight: 44 }}
                  >
                    Show all {total} ({hiddenCount} more)
                  </button>
                )}
                {!factFilter.trim() && expanded && total > LEARNED_GROUP_CAP && !(editingId && shown.slice(LEARNED_GROUP_CAP).some((f) => f.id === editingId)) && (
                  <button
                    onClick={() => setGroupExpanded(group.id, false)}
                    data-testid={`family-profile-show-fewer-${group.id}`}
                    className="font-body font-semibold hover:underline text-left"
                    style={{ fontSize: 13, color: "#54525C", minHeight: 44 }}
                  >
                    Show fewer
                  </button>
                )}

                {/* Inline add row */}
                {addingFor === group.id && (
                  <div
                    className="flex flex-col"
                    style={{ gap: 6, padding: "10px 12px", backgroundColor: "#F8F8FF", borderRadius: 10 }}
                  >
                    <input
                      type="text"
                      value={addKey}
                      placeholder="What is it? (e.g. Allergy)"
                      onChange={(e) => setAddKey(e.target.value)}
                      className="font-body bg-white w-full"
                      style={{
                        fontSize: 13,
                        color: "#1C1A3E",
                        border: "1px solid #C8C8F4",
                        borderRadius: 8,
                        padding: "6px 10px",
                        outline: "none",
                      }}
                    />
                    <div className="flex items-center" style={{ gap: 6 }}>
                      <input
                        type="text"
                        value={addValue}
                        placeholder="Details (e.g. Peanuts)"
                        onChange={(e) => setAddValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveAdd(group);
                          if (e.key === "Escape") setAddingFor(null);
                        }}
                        className="font-body bg-white flex-1 min-w-0"
                        style={{
                          fontSize: 13,
                          color: "#1C1A3E",
                          border: "1px solid #C8C8F4",
                          borderRadius: 8,
                          padding: "6px 10px",
                          outline: "none",
                        }}
                      />
                      <button
                        onClick={() => saveAdd(group)}
                        aria-label="Save fact"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#5050C8" }}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => setAddingFor(null)}
                        aria-label="Cancel"
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, color: "#54525C" }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Add a fact (Pro-gated) */}
              {addingFor !== group.id && (
                <button
                  onClick={() => startAdd(group)}
                  className="font-body font-semibold mt-3 hover:underline transition-all inline-flex items-center"
                  style={{ fontSize: 13, color: "#5050C8", gap: 4 }}
                >
                  {canEdit ? <Plus size={14} /> : <Lock size={12} />}
                  {canEdit ? "Add a fact" : "Comes with Kin Pro ✨"}
                </button>
              )}
            </div>
          ))}

          {/* ⚠️ #763 — the SECOND retired promise in this file, and the one
              easier to miss. It read "You can always view and delete what I
              remember for free right now." That was true yesterday and is not
              true now: a free account no longer sees Pro-collected facts at
              all. It sits under the group list, so a free account reaching it
              has just been shown her kids' birthdays and nothing else — the
              copy has to describe THAT screen. Same carve-out wording as the
              toast and the locked card. */}
          {!canEdit && (
            <p className="font-body" style={{ fontSize: 11, color: "#8B8B9E", lineHeight: 1.5 }}>
              💜 Names and birthdays are yours for free. Allergies, doctors,
              sizes and the rest come with Kin Pro ✨
            </p>
          )}
        </div>
      )}
    </section>
  );
}
